import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { MonthCard } from '../components/MonthCard';
import { CSVImport } from '../components/CSVImport';
import { PasteImport } from '../components/PasteImport';
import { AIImport } from '../components/AIImport';
import { dashboardApi, postsApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Plus, Calendar, Plane, Clock, ChevronRight, ChevronLeft, ClipboardPaste, Sparkles, Link2, Check, Copy, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { format } from 'date-fns';
import { WORKFLOW_COLORS, WORKFLOW_LABELS } from '../components/PostCard';
import { useClient } from '../context/ClientContext';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function DashboardPage({ readOnly = false }) {
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showPasteImport, setShowPasteImport] = useState(false);
  const [showAIImport, setShowAIImport] = useState(false);
  const [portalCopied, setPortalCopied] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  const { activeClient, resolvedLogoUrl } = useClient();

  const isClientView = readOnly || location.pathname.startsWith('/client');
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());

  const loadData = async () => {
    try {
      const [statsData, upcomingData] = await Promise.all([
        dashboardApi.getStats(),
        postsApi.getUpcoming(7)
      ]);
      setStats(statsData);
      setUpcoming(upcomingData.posts || []);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load dashboard data", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeClient, displayYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPostCountForMonth = (month, year) => {
    if (!stats?.months) return 0;
    const monthData = stats.months.find(m => m.month === month && m.year === year);
    return monthData?.count || 0;
  };

  const getNeedsReviewForMonth = (month, year) => {
    if (!stats?.months) return 0;
    const monthData = stats.months.find(m => m.month === month && m.year === year);
    return monthData?.needs_review || 0;
  };

  const handleImportComplete = () => {
    setShowImport(false);
    loadData();
  };

  return (
    <div className="min-h-screen page-background" data-testid="dashboard-page">
      <Navbar />

      {/* Compact Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, var(--brand-primary) 0%, color-mix(in srgb, var(--brand-primary) 85%, var(--brand-accent)) 60%, var(--brand-accent) 100%)`
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            {resolvedLogoUrl && (
              <img src={resolvedLogoUrl} alt={activeClient?.name || ''} className="h-10 w-auto drop-shadow-lg" />
            )}
            <div>
              <h1 className="text-lg md:text-xl font-bold text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {isClientView ? 'Content Calendar' : 'Social Media Calendar'}
              </h1>
              <p className="text-white/60 text-xs">
                {(() => {
                  const yearTotal = (stats?.months || [])
                    .filter(m => m.year === displayYear)
                    .reduce((sum, m) => sum + m.count, 0);
                  return `${yearTotal} posts planned for ${displayYear}`;
                })()}
                {isClientView && <span className="ml-2" style={{ color: 'var(--brand-secondary)', opacity: 0.8 }}>| View Only</span>}
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 relative z-10 pb-12">
        {/* Action Bar */}
        <div className="bg-white rounded-2xl shadow-xl shadow-black/5 p-4 mb-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#64748b] bg-[#f1f5f9] px-3 py-1.5 rounded-lg flex items-center gap-2">
              <button
                onClick={() => setDisplayYear(y => y - 1)}
                className="hover:text-[#1e293b] transition-colors"
                data-testid="year-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span data-testid="display-year">{displayYear}</span>
              <button
                onClick={() => setDisplayYear(y => y + 1)}
                className="hover:text-[#1e293b] transition-colors"
                data-testid="year-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </span>
            <span className="text-sm text-[#94a3b8]">
              {stats?.total_posts || 0} total posts
            </span>
            {!isClientView && stats?.pending_ideas > 0 && (
              <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full" data-testid="pending-ideas-badge">
                {stats.pending_ideas} idea{stats.pending_ideas !== 1 ? 's' : ''} pending
              </span>
            )}
          </div>
          {!isClientView && (
            <div className="flex items-center gap-2 flex-wrap">
              {activeClient?.portal_token && (
                <>
                  <Button
                    variant="outline"
                    className="rounded-xl px-3 gap-2 text-xs border-gray-200 text-gray-500 hover:text-gray-700"
                    data-testid="portal-link-btn"
                    onClick={() => {
                      const slug = activeClient.slug || activeClient.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || '';
                      const url = slug
                        ? `${window.location.origin}/portal/${slug}/${activeClient.portal_token}`
                        : `${window.location.origin}/portal/${activeClient.portal_token}`;
                      try {
                        navigator.clipboard.writeText(url);
                      } catch {
                        const ta = document.createElement('textarea');
                        ta.value = url;
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                      }
                      setPortalCopied(true);
                      setTimeout(() => setPortalCopied(false), 2000);
                      toast({ title: 'Portal link copied!', description: url });
                    }}
                  >
                    {portalCopied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {portalCopied ? 'Copied!' : 'Copy Link'}
                  </Button>
                  <a
                    href={(() => {
                      const slug = activeClient.slug || activeClient.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || '';
                      return slug
                        ? `${window.location.origin}/portal/${slug}/${activeClient.portal_token}`
                        : `${window.location.origin}/portal/${activeClient.portal_token}`;
                    })()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      variant="outline"
                      className="rounded-xl px-3 gap-2 text-xs border-gray-200 text-gray-500 hover:text-gray-700"
                      data-testid="portal-open-btn"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Open Portal
                    </Button>
                  </a>
                  <div className="h-6 w-px bg-gray-200" />
                </>
              )}
              <Button
                onClick={() => setShowAIImport(true)}
                className="rounded-xl px-4 text-white gap-2"
                style={{ backgroundColor: 'var(--brand-primary)' }}
                data-testid="ai-import-btn"
              >
                <Sparkles className="w-4 h-4" />
                AI Import
              </Button>
              <Button
                onClick={() => setShowPasteImport(true)}
                variant="outline"
                className="rounded-xl px-4"
                style={{ borderColor: 'var(--brand-secondary)', color: 'var(--brand-secondary)' }}
                data-testid="paste-import-btn"
              >
                <ClipboardPaste className="w-4 h-4 mr-2" />
                Paste & Import
              </Button>
              <Button
                onClick={() => setShowImport(!showImport)}
                className={`${showImport ? 'btn-secondary' : 'btn-primary'} rounded-xl px-5`}
                data-testid="toggle-import-btn"
              >
                <Plus className={`w-4 h-4 mr-2 ${showImport ? 'rotate-45' : ''} transition-transform`} />
                {showImport ? 'Close' : 'Import File'}
              </Button>
            </div>
          )}
        </div>

        {/* CSV Import Section */}
        {!isClientView && showImport && (
          <div className="mb-8 animate-fade-in">
            <CSVImport onImportComplete={handleImportComplete} />
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-dashboard">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-[#2563eb] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-[#64748b] text-sm font-medium">Loading calendar...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Week at a Glance */}
            {upcoming.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl shadow-black/5 p-6 mb-8" data-testid="week-at-a-glance">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" style={{ color: 'var(--brand-accent)' }} />
                    <h2 className="text-base font-bold text-[#1e293b]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      This Week
                    </h2>
                    <span className="text-xs text-[#94a3b8] font-medium">{upcoming.length} upcoming</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {upcoming.slice(0, 8).map(post => {
                    const ws = post.workflow_status || 'draft';
                    const wStyle = WORKFLOW_COLORS[ws] || WORKFLOW_COLORS.draft;
                    return (
                      <div key={post.id} className="flex items-start gap-3 p-3 rounded-xl border border-[#e2e8f0] transition-colors" style={{ '--hover-border': 'var(--brand-accent)' }} data-testid={`upcoming-post-${post.id}`}>
                        <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${wStyle.dot}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-[#1e293b] truncate">{post.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[11px] text-[#64748b]">
                              {format(new Date(post.scheduled_date), 'EEE, MMM d')}
                            </span>
                            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${wStyle.badge}`}>
                              {WORKFLOW_LABELS[ws]}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Month Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="months-grid">
              {MONTHS.map((monthName, index) => (
                <MonthCard
                  key={index}
                  month={index + 1}
                  year={displayYear}
                  postCount={getPostCountForMonth(index + 1, displayYear)}
                  needsReview={isClientView ? 0 : getNeedsReviewForMonth(index + 1, displayYear)}
                  index={index}
                  basePath={isClientView ? '/client/month' : '/month'}
                />
              ))}
            </div>

            {/* Empty State */}
            {stats?.total_posts === 0 && !isClientView && (
              <div className="text-center py-16 mt-10 bg-white rounded-2xl border-2 border-dashed border-[#e2e8f0]" data-testid="empty-state">
                <Calendar className="w-12 h-12 text-[#cbd5e1] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#1e293b] mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  No posts yet
                </h3>
                <p className="text-[#64748b] mb-6 text-sm">
                  Import a CSV file or click on any month to start adding posts
                </p>
                <Button onClick={() => setShowImport(true)} className="btn-primary rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />
                  Import Your First Posts
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Paste Import Modal */}
      <PasteImport
        isOpen={showPasteImport}
        onClose={() => setShowPasteImport(false)}
        onImportComplete={handleImportComplete}
      />

      {/* AI Import Modal */}
      <AIImport
        open={showAIImport}
        onOpenChange={setShowAIImport}
        onImportComplete={handleImportComplete}
        currentMonth={new Date().getMonth() + 1}
        currentYear={displayYear}
      />
    </div>
  );
}
